import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, MapPin, Clock, ChevronRight, Navigation, Users, 
  Calendar, Mail, Bell, Check, Flame, Play, ArrowRight,
  TrendingUp, Star, Filter, User, Zap
} from 'lucide-react';
import { HeroMap, SessionsMap } from '../components/LeafletMap';
import { useAuth } from '../context/AuthContext';
import AnimatedLogo, { HeaderLogo } from '../components/AnimatedLogo';
import { useTranslation } from '../i18n';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity categories with icons
const categories = [
  { id: 'yoga', name: 'Yoga', icon: '🧘', color: '#9B59B6' },
  { id: 'renforcement', name: 'Renforcement', icon: '💪', color: '#E74C3C' },
  { id: 'running', name: 'Running', icon: '🏃', color: '#3498DB' },
  { id: 'padel', name: 'Padel', iconUrl: 'https://customer-assets.emergentagent.com/job_3a166f7e-e5bc-468f-bcb2-a7ddc9232714/artifacts/gy5kvs35_a_clean_vibrant_emoji_style_illustration_of_a_pad.png', color: '#2ECC71' },
  { id: 'tennis', name: 'Tennis', icon: '🎾', color: '#F1C40F' },
  { id: 'cardio', name: 'Cardio', icon: '❤️', color: '#E91E63' },
  { id: 'pilates', name: 'Pilates', icon: '🎯', color: '#FF9800' },
  { id: 'outdoor', name: 'Outdoor', icon: '🌲', color: '#27AE60' },
];

// Animated counter component
const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setDisplayValue(Math.floor(progress * value));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  
  return <span>{displayValue.toLocaleString()}</span>;
};

// Countdown component for sessions starting soon
const Countdown = ({ startTime }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!startTime) return 'Bientôt';
      
      const now = new Date();
      const start = new Date(startTime);
      
      // Check if date is valid
      if (isNaN(start.getTime())) return 'Bientôt';
      
      const diff = start - now;
      
      if (diff <= 0) return 'Maintenant';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}min`;
      }
      return `${minutes} min`;
    };
    
    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => setTimeLeft(calculateTimeLeft()), 60000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  return <span className="font-bold text-primary">{timeLeft}</span>;
};

// Session Card Component - Action-oriented
const SessionCard = ({ session }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Use category or activity_type field
  const activityType = session.category || session.activity_type || 'sport';
  const category = categories.find(c => c.id === activityType.toLowerCase()) || categories[0];
  
  // Get session time - handle different field names
  const sessionTime = session.start_time || session.time || '';
  const formatTime = (time) => {
    if (!time) return '';
    if (time.includes(':')) {
      return time.substring(0, 5);
    }
    return time;
  };
  
  // Get display title - use title or activity type
  const displayTitle = session.title || activityType.charAt(0).toUpperCase() + activityType.slice(1);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border-2 border-gray-100 rounded-xl overflow-hidden hover:border-cyan-300 hover:shadow-lg transition-all group cursor-pointer"
      onClick={() => navigate(`/session/${session.session_id}`)}
      data-testid={`session-card-${session.session_id}`}
    >
      <div className="p-4">
        {/* Activity + Time */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {category.iconUrl ? (
              <img src={category.iconUrl} alt={category.name} className="w-6 h-6" />
            ) : (
              <span className="text-xl">{category.icon}</span>
            )}
            <span className="font-semibold text-sm text-gray-900">{displayTitle}</span>
          </div>
          <div className="flex items-center gap-1 text-sm bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3" />
            <span className="font-bold">{session.starts_in_text || formatTime(sessionTime)}</span>
          </div>
        </div>
        
        {/* Coach */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden">
            {(session.coach_photo || session.coach_avatar) ? (
              <img src={session.coach_photo || session.coach_avatar} alt="Coach" className="w-full h-full object-cover" />
            ) : (
              <User className="w-full h-full p-1.5 text-gray-400" />
            )}
          </div>
          <span className="text-sm text-gray-600">{session.coach_name || 'Coach certifié'}</span>
        </div>
        
        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{session.location || 'Bruxelles'}</span>
        </div>
        
        {/* Participant Avatars */}
        {session.participant_avatars && session.participant_avatars.length > 0 && (
          <div className="mt-3 flex items-center gap-2">
            <div className="flex -space-x-2">
              {session.participant_avatars.slice(0, 3).map((participant, idx) => (
                <div 
                  key={idx}
                  className="w-6 h-6 rounded-full border-2 border-white overflow-hidden bg-gray-200"
                  title={participant.name}
                >
                  {participant.picture ? (
                    <img src={participant.picture} alt={participant.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-white font-medium bg-gradient-to-br from-cyan-500 to-emerald-500">
                      {participant.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
              ))}
              {session.current_participants > 3 && (
                <div className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-xs text-gray-600 font-medium">
                  +{session.current_participants - 3}
                </div>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {session.current_participants} inscrit{session.current_participants > 1 ? 's' : ''}
            </span>
          </div>
        )}
        
        {/* Bottom row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <Users className="w-4 h-4" />
              <span>{session.spots_left || session.max_participants - (session.current_participants || 0)} places</span>
            </div>
            <span className="font-bold text-lg text-gray-900">{session.price}€</span>
          </div>
          <Button 
            size="sm" 
            className="btn-primary"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/session/${session.session_id}`);
            }}
            data-testid={`join-session-${session.session_id}`}
          >
            {t('sessions.join')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Coach Card Component - with larger avatar
const CoachCard = ({ coach, onClick, onViewSessions }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-cyan-300 hover:shadow-xl transition-all cursor-pointer text-center group"
      onClick={onClick}
      data-testid={`coach-card-${coach.user_id}`}
    >
      {/* Large avatar with ring */}
      <div className="relative mx-auto mb-4">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-100 to-emerald-100 p-1 mx-auto">
          <div className="w-full h-full rounded-full bg-white overflow-hidden">
            {coach.avatar_url ? (
              <img src={coach.avatar_url} alt={coach.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <User className="w-10 h-10 text-gray-400" />
              </div>
            )}
          </div>
        </div>
        {/* Verified badge */}
        {coach.is_verified && (
          <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-cyan-500 rounded-full flex items-center justify-center border-3 border-white shadow-md">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
      
      <h4 className="font-bold text-lg mb-1 text-gray-900 group-hover:text-cyan-600 transition-colors">{coach.name}</h4>
      <p className="text-sm text-gray-500 mb-3 line-clamp-1">
        {coach.specialties?.slice(0, 2).join(' • ') || 'Coach sportif'}
      </p>
      
      <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-full">
          <Calendar className="w-3.5 h-3.5 text-cyan-500" />
          <span className="font-medium">{coach.sessions_count || 0}</span>
        </span>
        <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-full">
          <Users className="w-3.5 h-3.5 text-emerald-500" />
          <span className="font-medium">{coach.participants_count || 0}</span>
        </span>
      </div>
      
      <Button 
        variant="outline" 
        size="sm" 
        className="w-full border-cyan-200 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-300 font-medium"
        onClick={(e) => {
          e.stopPropagation();
          if (onViewSessions) onViewSessions(coach);
        }}
        data-testid={`view-sessions-${coach.user_id}`}
      >
        {t('sessions.viewCoachSessions')}
      </Button>
    </motion.div>
  );
};

// Community Card Component - with activity-based icons
const communityIcons = {
  padel: { emoji: '🎾', color: 'from-green-400 to-emerald-500' },
  tennis: { emoji: '🎾', color: 'from-yellow-400 to-orange-500' },
  yoga: { emoji: '🧘', color: 'from-purple-400 to-violet-500' },
  pilates: { emoji: '🎯', color: 'from-pink-400 to-rose-500' },
  running: { emoji: '🏃', color: 'from-blue-400 to-cyan-500' },
  fitness: { emoji: '💪', color: 'from-red-400 to-orange-500' },
  default: { emoji: '🏆', color: 'from-cyan-400 to-teal-500' }
};

const CommunityCard = ({ community }) => {
  const { t } = useTranslation();
  // Determine icon based on community name or activity type
  const getIconConfig = () => {
    const name = (community.name || '').toLowerCase();
    if (name.includes('padel')) return communityIcons.padel;
    if (name.includes('tennis')) return communityIcons.tennis;
    if (name.includes('yoga')) return communityIcons.yoga;
    if (name.includes('pilates')) return communityIcons.pilates;
    if (name.includes('running') || name.includes('course')) return communityIcons.running;
    if (name.includes('fitness') || name.includes('musculation')) return communityIcons.fitness;
    return communityIcons.default;
  };
  
  const iconConfig = getIconConfig();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      className="bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-cyan-300 hover:shadow-xl transition-all"
    >
      <div className="flex items-start gap-4 mb-4">
        {/* Activity icon with gradient */}
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${iconConfig.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
          <span className="text-2xl">{iconConfig.emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 mb-1 truncate">{community.name}</h4>
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Users className="w-4 h-4 text-cyan-500" />
            <span className="font-medium">{community.members} membres</span>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{community.description}</p>
      
      <Button variant="outline" size="sm" className="w-full border-cyan-200 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-300 font-medium">
        <Users className="w-4 h-4 mr-2" />
        {t('landing.joinGroup')}
      </Button>
    </motion.div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t, lang } = useTranslation();
  
  // Location state
  const [userLocation, setUserLocation] = useState(null);
  const [userCity, setUserCity] = useState('Bruxelles');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data state
  const [sessionsStartingSoon, setSessionsStartingSoon] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [featuredCoaches, setFeaturedCoaches] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state for map
  const [mapFilter, setMapFilter] = useState('today');
  
  // Scroll position for parallax
  const [scrollY, setScrollY] = useState(0);
  
  // Stats state
  const [stats, setStats] = useState({
    sessions_next_3h: 81,
    total_participants: 3567,
    coaches_count: 114,
    sessions_this_week: 320
  });
  
  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  
  // Selected activity filter
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Track scroll for parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          reverseGeocode(loc.lat, loc.lng);
        },
        () => {
          setUserLocation({ lat: 50.8503, lng: 4.3517 });
          setUserCity('Bruxelles');
        },
        { timeout: 5000 }
      );
    } else {
      setUserLocation({ lat: 50.8503, lng: 4.3517 });
    }
  }, []);

  // Reverse geocode
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      const city = data.address?.city || data.address?.town || data.address?.municipality || 'Bruxelles';
      setUserCity(city);
    } catch {
      setUserCity('Bruxelles');
    }
  };

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch sessions starting soon
        const soonRes = await fetch(
          `${API_URL}/api/sessions/starting-soon?city=${encodeURIComponent(userCity)}&lat=${userLocation?.lat || 50.8503}&lng=${userLocation?.lng || 4.3517}&limit=8`
        );
        if (soonRes.ok) {
          const data = await soonRes.json();
          setSessionsStartingSoon(data);
        }
        
        // Fetch all sessions for map
        const sessionsRes = await fetch(`${API_URL}/api/sessions?page_size=500`);
        if (sessionsRes.ok) {
          const data = await sessionsRes.json();
          // Handle paginated response
          setAllSessions(data.items || data);
        }
        
        // Fetch featured coaches
        const coachesRes = await fetch(
          `${API_URL}/api/coaches/featured?city=${encodeURIComponent(userCity)}&limit=6`
        );
        if (coachesRes.ok) {
          const data = await coachesRes.json();
          setFeaturedCoaches(data);
        }
        
        // Fetch communities
        const commRes = await fetch(
          `${API_URL}/api/communities?city=${encodeURIComponent(userCity)}`
        );
        if (commRes.ok) {
          const data = await commRes.json();
          setCommunities(data.slice(0, 4));
        }
        
        // Fetch realtime stats
        const statsRes = await fetch(
          `${API_URL}/api/stats/realtime?city=${encodeURIComponent(userCity)}`
        );
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(prev => ({
            ...prev,
            sessions_next_3h: data.sessions_starting_soon || 81,
            total_participants: data.participants_today || 78
          }));
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (userLocation) {
      fetchData();
    }
  }, [userLocation, userCity]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/explore');
    }
  };

  // Handle newsletter subscription
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/community/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          city: userCity,
          preferences: [],
          language: lang
        })
      });
      
      if (response.ok) {
        setSubscribed(true);
        toast.success('Inscription réussie !');
      }
    } catch {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubscribing(false);
    }
  };

  // Filter sessions by activity
  const filteredSessions = selectedActivity
    ? sessionsStartingSoon.filter(s => s.activity_type?.toLowerCase() === selectedActivity)
    : sessionsStartingSoon;

  return (
    <div className="min-h-screen bg-background">
      {/* ==================== HEADER ==================== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <HeaderLogo inverted={false} />
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/explore" className="text-sm text-gray-700 hover:text-primary transition-colors font-medium">
                {t('nav.explore')}
              </Link>
              <Link to="/outfit" className="text-sm text-gray-700 hover:text-primary transition-colors font-medium" data-testid="nav-outfit">
                {t('nav.outfit')}
              </Link>
              <Link to="/become-coach" className="text-sm text-gray-700 hover:text-primary transition-colors font-medium">
                {t('nav.becomeCoach')}
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <LanguageSelector />
              {isAuthenticated ? (
                <Button onClick={() => navigate('/profile')} variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                  {user?.name?.split(' ')[0] || t('nav.profile')}
                </Button>
              ) : (
                <>
                  <Button onClick={() => navigate('/login')} variant="ghost" size="sm" className="text-gray-700 hover:bg-gray-100">
                    {t('nav.login')}
                  </Button>
                  <Button onClick={() => navigate('/register')} className="btn-primary" size="sm">
                    {t('nav.register')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[100vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Parallax Map Background - MORE VISIBLE */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y: scrollY * 0.3 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 1.5 }}
        >
          <HeroMap className="w-full h-[130%] -mt-20" />
        </motion.div>
        
        {/* Lighter gradient overlay to show map better */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-white/60 via-cyan-50/50 to-white/70" />
        
        {/* Animated gradient orbs with parallax */}
        <motion.div 
          className="absolute top-10 left-5 w-72 h-72 bg-gradient-to-br from-cyan-400/30 to-blue-500/20 rounded-full blur-3xl"
          style={{ y: scrollY * 0.3 }}
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 20, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-emerald-400/25 to-teal-500/15 rounded-full blur-3xl"
          style={{ y: scrollY * 0.2 }}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.25, 0.4, 0.25],
            x: [0, -30, 0]
          }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* MEGA Animated Logo - MUCH BIGGER & MORE DYNAMIC */}
            <motion.div
              initial={{ opacity: 0, scale: 0.3, y: -60, rotateY: -180 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateY: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="mb-4"
            >
              <motion.div
                animate={{ 
                  y: [0, -15, 0],
                  scale: [1, 1.05, 1],
                  rotateZ: [0, 2, -2, 0],
                  filter: [
                    'drop-shadow(0 25px 50px rgba(6, 182, 212, 0.4))',
                    'drop-shadow(0 40px 80px rgba(6, 182, 212, 0.6))',
                    'drop-shadow(0 25px 50px rgba(6, 182, 212, 0.4))'
                  ]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block"
              >
                <motion.img 
                  src="/logo.png" 
                  alt="Motivya" 
                  className="h-48 sm:h-56 md:h-72 lg:h-96 w-auto mx-auto"
                  data-testid="hero-logo"
                  whileHover={{ 
                    scale: 1.1,
                    rotateZ: 5,
                    transition: { duration: 0.3 }
                  }}
                />
              </motion.div>
              
              {/* Glow ring behind logo */}
              <motion.div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 sm:w-80 md:w-96 lg:w-[500px] h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-full pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, rgba(6,182,212,0.15) 0%, rgba(16,185,129,0.1) 40%, transparent 70%)'
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
            
            {/* Location badge */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/95 backdrop-blur-sm border-2 border-cyan-400 text-cyan-700 text-sm mb-6 shadow-xl"
            >
              <Navigation className="w-5 h-5 animate-pulse" />
              <span className="font-bold text-base">{userCity}</span>
              <span className="w-3 h-3 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-500/50" />
            </motion.div>
            
            {/* Main title */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-4 leading-tight text-gray-900">
              {t('landing.heroTitle').split(' ').slice(0, -2).join(' ')}<br />
              <span className="bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 bg-clip-text text-transparent">{t('landing.heroTitle').split(' ').slice(-2).join(' ')}</span>
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto font-medium">
              {t('landing.heroDesc')}
            </p>
            
            {/* Search form */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6">
              <div className="flex gap-2 p-2.5 bg-white shadow-2xl border-2 border-gray-200 rounded-2xl hover:border-cyan-300 transition-colors">
                <div className="flex-1 relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-cyan-500" />
                  <Input
                    type="text"
                    placeholder={t('landing.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-14 h-14 bg-transparent border-0 focus-visible:ring-0 text-lg text-gray-800 placeholder:text-gray-400"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="btn-primary h-14 px-8 text-base font-bold shadow-lg shadow-cyan-500/30" data-testid="hero-search-btn">
                  <Search className="w-5 h-5 mr-2" />
                  {t('landing.nearMe')}
                </Button>
              </div>
            </form>
            
            {/* Secondary CTA */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/explore')}
              className="text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 text-base"
              data-testid="explore-all-btn"
            >
              {t('landing.allActivities')}
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            
            {/* Live indicator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="mt-10 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-200 shadow-lg"
            >
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <Zap className="w-5 h-5" />
              <span className="font-bold text-base">{stats.sessions_next_3h} {t('landing.sessionsStartingIn3h')} {userCity}</span>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronRight className="w-8 h-8 text-gray-400 rotate-90" />
        </motion.div>
      </section>

      {/* ==================== SECTION 1: STARTING SOON ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="starting-soon-section">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame className="w-6 h-6 text-orange-500" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.startingSoonTitle')}</h2>
              </div>
              <p className="text-gray-600">
                <span className="text-cyan-600 font-semibold">{stats.sessions_next_3h}</span> {t('landing.sessionsStartingIn3h')} {userCity}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/explore')} className="hidden sm:flex border-gray-300 text-gray-700 hover:bg-gray-50">
              {t('landing.allActivities')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {/* Session grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-gray-100 border border-gray-200 rounded-xl p-4 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredSessions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredSessions.slice(0, 8).map(session => (
                <SessionCard key={session.session_id} session={session} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Aucune séance dans les prochaines heures</p>
              <Button onClick={() => navigate('/explore')} className="mt-4 btn-primary">
                Explorer toutes les séances
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ==================== SECTION 2: INTERACTIVE MAP ==================== */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('landing.activitiesAroundYou')}</h2>
            
            {/* Quick filters */}
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'today', label: t('landing.filterToday') },
                { id: 'tonight', label: t('landing.filterTonight') },
                { id: 'week', label: t('landing.filterWeek') },
                { id: 'nearby', label: t('landing.filterNearby') },
              ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setMapFilter(filter.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    mapFilter === filter.id
                      ? 'bg-cyan-500 text-white shadow-lg'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-cyan-300 hover:text-cyan-600'
                  }`}
                  data-testid={`filter-${filter.id}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Map container */}
          <div className="h-[500px] rounded-2xl overflow-hidden border-2 border-gray-200 shadow-xl">
            {userLocation ? (
              <SessionsMap 
                sessions={allSessions} 
                center={[userLocation.lat, userLocation.lng]} 
                className="w-full h-full"
                mapStyle="light"
                showRadius={true}
                radiusKm={2}
                showUserLocation={true}
                zoom={14}
              />
            ) : (
              <SessionsMap 
                sessions={allSessions} 
                center={[50.8503, 4.3517]} 
                className="w-full h-full"
                mapStyle="light"
                showRadius={true}
                radiusKm={2}
                showUserLocation={true}
                zoom={13}
              />
            )}
          </div>
          
          {/* Distance info */}
          <p className="text-center text-sm text-gray-500 mt-3">
            {t('landing.mapRadiusInfo')}
          </p>
        </div>
      </section>

      {/* ==================== SECTION 3: POPULAR ACTIVITIES ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="activities-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center text-gray-900">{t('landing.popularActivities')}</h2>
          
          {/* Activity buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedActivity(selectedActivity === cat.id ? null : cat.id);
                }}
                className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                  selectedActivity === cat.id
                    ? 'text-white scale-105 shadow-lg'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-cyan-300 hover:scale-105 hover:shadow-md'
                }`}
                style={selectedActivity === cat.id ? { backgroundColor: cat.color } : {}}
                data-testid={`activity-${cat.id}`}
              >
                {cat.iconUrl ? (
                  <img src={cat.iconUrl} alt={cat.name} className="w-5 h-5" />
                ) : (
                  <span className="text-lg">{cat.icon}</span>
                )}
                {cat.name}
              </button>
            ))}
          </div>
          
          {/* Show filtered sessions when activity selected */}
          {selectedActivity && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              {allSessions
                .filter(s => s.activity_type?.toLowerCase() === selectedActivity)
                .slice(0, 4)
                .map(session => (
                  <SessionCard key={session.session_id} session={session} />
                ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ==================== SECTION 4: LOCAL COACHES ==================== */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-testid="coaches-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">{t('landing.neighborhoodCoaches')}</h2>
              <p className="text-gray-600">{t('landing.activeCoachesNear')} {userCity}</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/explore?view=coaches')} className="border-gray-300 text-gray-700 hover:bg-gray-50">
              {t('landing.allCoaches')}
            </Button>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {featuredCoaches.slice(0, 6).map(coach => (
              <CoachCard 
                key={coach.user_id} 
                coach={coach}
                onClick={() => navigate(`/coaches/${coach.user_id}`)}
                onViewSessions={(c) => navigate(`/explore?coach=${c.user_id}&coach_name=${encodeURIComponent(c.name)}`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 5: COMMUNITIES ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="communities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">{t('landing.sportsCommunities')}</h2>
            <p className="text-gray-600">{t('landing.joinGroupRegularly')}</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {communities.map((community, index) => (
              <CommunityCard key={index} community={community} />
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 6: FOR COACHES ==================== */}
      <section className="py-20 px-4 bg-gradient-to-br from-cyan-500 via-cyan-600 to-emerald-500" data-testid="coach-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-white">
              {t('landing.developCommunity')}
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              {t('landing.joinMotivy')}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {[
                { icon: <Users className="w-8 h-8" />, text: t('landing.fillSessions') },
                { icon: <MapPin className="w-8 h-8" />, text: t('landing.findNearbyAthletes') },
                { icon: <TrendingUp className="w-8 h-8" />, text: t('landing.createRecurringIncome') },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center gap-3 p-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white">
                    {item.icon}
                  </div>
                  <span className="font-medium text-white">{item.text}</span>
                </div>
              ))}
            </div>
            
            <Button 
              onClick={() => navigate('/become-coach')} 
              className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-full font-bold shadow-xl hover:scale-105 transition-all"
              data-testid="become-coach-cta"
            >
              {t('landing.becomePartnerCoach')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ==================== SECTION 7: SOCIAL PROOF / STATS ==================== */}
      <section className="py-16 px-4 bg-white" data-testid="stats-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {[
              { value: stats.total_participants, label: t('landing.activeParticipants'), icon: <Users className="w-6 h-6" />, color: 'cyan' },
              { value: stats.coaches_count, label: t('landing.certifiedCoaches'), icon: <Star className="w-6 h-6" />, color: 'yellow' },
              { value: stats.sessions_this_week, label: t('landing.sessionsThisWeekLabel'), icon: <Calendar className="w-6 h-6" />, color: 'emerald' },
              { value: 19, label: t('landing.coveredMunicipalities'), icon: <MapPin className="w-6 h-6" />, color: 'purple' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 rounded-2xl bg-gray-50 border border-gray-100"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  stat.color === 'cyan' ? 'bg-cyan-100 text-cyan-600' :
                  stat.color === 'yellow' ? 'bg-yellow-100 text-yellow-600' :
                  stat.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {stat.icon}
                </div>
                <div className="text-3xl sm:text-4xl font-bold mb-1 text-gray-900">
                  <AnimatedCounter value={stat.value} />
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== SECTION 8: NEWSLETTER ==================== */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-testid="newsletter-section">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-cyan-600" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">
            {t('landing.receiveSessionsNearYou')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('landing.beNotifiedNewSessions')}
          </p>
          
          {subscribed ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600 bg-emerald-50 py-3 px-6 rounded-full">
              <Check className="w-5 h-5" />
              <span className="font-medium">{t('landing.thankYouSubscribed')}</span>
            </div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder={t('landing.yourEmail')}
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="flex-1 border-gray-300 focus:border-cyan-500"
                data-testid="newsletter-email"
              />
              <Button type="submit" className="btn-primary" disabled={subscribing} data-testid="newsletter-submit">
                {subscribing ? '...' : t('landing.subscribeBtn')}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-semibold mb-4 text-white">Motivya</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/explore" className="hover:text-white transition-colors">{t('landing.footerExplore')}</Link></li>
                <li><Link to="/become-coach" className="hover:text-white transition-colors">{t('landing.footerCoach')}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">{t('landing.footerContact')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('landing.footerActivities')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                {categories.slice(0, 4).map(cat => (
                  <li key={cat.id}>
                    <Link to={`/explore?activity=${cat.id}`} className="hover:text-white transition-colors">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('landing.footerLegal')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('landing.footerPrivacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('landing.footerTerms')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-white">{t('landing.footerContact')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="mailto:contact@motivya.be" className="hover:text-cyan-400 transition-colors">
                    contact@motivya.be
                  </a>
                </li>
                <li>{t('landing.footerLocation')}</li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © 2026 Motivya. {t('landing.allRightsReserved')}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{t('landing.footerSlogan')}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
